import { addDays, isValid, parseISO } from 'date-fns';

import { DEFAULT_DATE_FORMAT } from '@iconik-io/core/src/date/date_formats';
import { format } from '@iconik-io/core/src/date/format';
import { getTimezoneOffset } from '@iconik-io/core/src/get_timezone_offset';
import type {
  SearchCriteriaFilterSchema,
  SearchCriteriaTerm,
} from '@iconik-io/iconik-api/src/services/search';

import type { FilterValues } from '../../redux/common_filters/types';
import { buildMultiSelectFilterCriteria } from './build_multi_select_filter_criteria';
import { guardFilterGeneric } from './guard_filter_generic';
import { guardFilterRange } from './guard_filter_range';
import { guardFilterString } from './guard_filter_string';
import { guardMultiSelectFilter } from './guard_multi_select_filter';

export const filterValuesToCriteriaFilter = (
  filterValues: FilterValues,
  additionalRawFilters: SearchCriteriaFilterSchema[],
): SearchCriteriaFilterSchema | undefined => {
  const filter: SearchCriteriaFilterSchema = {
    operator: 'AND',
  };
  const filterTerms: SearchCriteriaTerm[] = [];
  const filterFilters: SearchCriteriaFilterSchema[] = [];

  for (const [name, values] of Object.entries(filterValues)) {
    const multiSelectFilter = guardMultiSelectFilter(values, {
      convertLegacyStrings: false,
    });
    if (multiSelectFilter) {
      const components = buildMultiSelectFilterCriteria(
        multiSelectFilter,
        name,
      );
      filterTerms.push(...components.terms);
      filterFilters.push(...components.filters);
      continue;
    }

    const genericFilter = guardFilterGeneric(values);
    const stringFilter = guardFilterString(values);
    const rangeFilter = guardFilterRange(values);

    if (genericFilter && genericFilter.length > 0) {
      if (
        name.startsWith('metadata.') &&
        genericFilter.length === 1 &&
        genericFilter[0] === 'false'
      ) {
        filterFilters.push({
          operator: 'OR',
          terms: [
            { name, value: 'false' },
            { name, exists: false },
          ],
        });
      } else {
        filterTerms.push({ name, value_in: genericFilter });
      }
      continue;
    }
    if (stringFilter) {
      filterTerms.push({ name, value: stringFilter });
      continue;
    }
    if (rangeFilter) {
      let filterTerm: SearchCriteriaTerm = { name, range: rangeFilter };

      if (name === 'date_created' || name === 'date_deleted') {
        // inject client timezone into range date filter
        filterTerm = {
          ...filterTerm,
          range: {
            ...filterTerm.range,
            timezone: getTimezoneOffset(),
          },
        };
      }

      if (filterTerm.range && filterTerm.range.min === filterTerm.range.max) {
        // if min and max are the same, use value instead of range
        // https://linear.app/iconik/issue/FE-861/metadata-date-filter-doesnt-work
        filterTerm = {
          name,
          value: filterTerm.range.min ?? undefined,
        };
      }

      if (
        filterTerm.range &&
        name.startsWith('metadata.') &&
        filterTerm.range.max &&
        /^\d{4}-\d{2}-\d{2}$/.test(filterTerm.range.max)
      ) {
        // The backend max is non-inclusive. For plain date ranges (yyyy-MM-dd),
        // increment max by 1 day so the entire last selected day is included.
        const parsedMax = parseISO(filterTerm.range.max);
        if (isValid(parsedMax)) {
          filterTerm = {
            ...filterTerm,
            range: {
              ...filterTerm.range,
              max:
                format(addDays(parsedMax, 1), DEFAULT_DATE_FORMAT) ?? undefined,
            },
          };
        }
      }

      filterTerms.push(filterTerm);
    }
  }

  if (filterTerms.length > 0) {
    filter.terms = filterTerms;
  }

  if (filterFilters.length > 0) {
    filter.filters = filterFilters;
  }

  if (additionalRawFilters.length > 0) {
    if (!filter.filters) {
      filter.filters = [];
    }
    filter.filters = filter.filters.concat(additionalRawFilters);
  }

  if (!filter.terms && !filter.filters) {
    return undefined;
  }

  return filter;
};
